import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { computeSemNgrams } from "../utils/semNgrams";

export default function GrowthPointsView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  if (!proj) return null;
  const store = getStore(activeId);
  const clusters = store.clusters || [];

  const [gscRows, setGscRows] = useState([]);
  const [seoTexts, setSeoTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedPages, setExpandedPages] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("impact");
  const [searchQ, setSearchQ] = useState("");
  const [page, setPage] = useState(0);
  const PER_PAGE = 20;

  // Load GSC data + SEO texts
  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    Promise.all([
      apiFetch(`/api/gsc/saved/${activeId}`).then(r => r.ok ? r.json() : []).catch(() => []),
      apiFetch(`/api/projects/${activeId}/seo-texts`).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([gsc, texts]) => {
      const gscData = gsc?.found && Array.isArray(gsc.rows) ? gsc.rows : (Array.isArray(gsc) ? gsc : []);
      setGscRows(gscData);
      const textsMap = {};
      (Array.isArray(texts) ? texts : []).forEach(row => { if (row.url) textsMap[row.url] = row; });
      setSeoTexts(textsMap);
      setLoading(false);
    });
  }, [activeId, apiFetch]);

  const semNgrams = useMemo(() => computeSemNgrams(clusters), [clusters]);

  // 1. Site CTR curve — median CTR per position 1-20
  const ctrCurve = useMemo(() => {
    const byPos = {};
    gscRows.forEach(r => {
      const p = Math.round(r.position);
      if (p >= 1 && p <= 20) {
        if (!byPos[p]) byPos[p] = [];
        byPos[p].push(r.ctr || 0);
      }
    });
    const curve = {};
    for (let pos = 1; pos <= 20; pos++) {
      const vals = (byPos[pos] || []).sort((a, b) => a - b);
      curve[pos] = vals.length ? vals[Math.floor(vals.length / 2)] : 0;
    }
    return curve;
  }, [gscRows]);

  // 2. Page meta map from clusters
  const pageMeta = useMemo(() => {
    const map = {};
    clusters.forEach(c => {
      if (!c.url) return;
      if (!map[c.url]) {
        map[c.url] = {
          title: c.meta?.title || "",
          h1: c.meta?.h1 || "",
          description: c.meta?.description || "",
          keywords: []
        };
      }
      (c.keywords || []).forEach(k => {
        const kw = typeof k === "string" ? k : k.keyword || "";
        if (kw) map[c.url].keywords.push(kw.toLowerCase());
      });
    });
    return map;
  }, [clusters]);

  // 3. Core keywords set
  const coreKeywords = useMemo(() => {
    const s = new Set();
    clusters.forEach(c => {
      (c.keywords || []).forEach(k => {
        const kw = typeof k === "string" ? k : k.keyword || "";
        if (kw) s.add(kw.toLowerCase());
      });
    });
    return s;
  }, [clusters]);

  // 4. All N-grams set for linking analysis
  const pageNgramSets = useMemo(() => {
    if (!semNgrams || !semNgrams.byPage) return {};
    const sets = {};
    for (const [url, data] of semNgrams.byPage) {
      const highGrams = new Set();
      const bigrams = data.bigrams || [];
      const trigrams = data.trigrams || [];
      bigrams.slice(0, 30).forEach(g => highGrams.add(g.gram || ""));
      trigrams.slice(0, 30).forEach(g => highGrams.add(g.gram || ""));
      sets[url] = highGrams;
    }
    return sets;
  }, [semNgrams]);

  // 5. Generate recommendations
  const recommendations = useMemo(() => {
    if (!gscRows.length) return [];
    const recs = [];
    let recId = 0;

    const pageUrls = [...new Set(gscRows.map(r => r.page))];
    const hasSeoTexts = Object.keys(seoTexts).length > 0;

    gscRows.forEach(r => {
      const pos = r.position || 0;
      const imp = r.impressions || 0;
      const ctr = r.ctr || 0;
      const query = (r.query || "").toLowerCase();
      const pageUrl = r.page || "";
      const roundPos = Math.round(pos);
      const medianCtr = ctrCurve[roundPos] || 0;
      const meta = pageMeta[pageUrl] || {};
      const seoText = seoTexts[pageUrl] || null;
      const textContent = (seoText?.html_content || seoText?.content || "").toLowerCase();

      // Scenario 1: Snippet — pos 1-5, low CTR
      if (pos >= 1 && pos <= 5 && ctr < medianCtr && medianCtr > 0) {
        const ctrGap = medianCtr - ctr;
        const impact = Math.round(imp * ctrGap / 100);
        const actions = [];
        const details = [];

        const titleLow = (meta.title || "").toLowerCase();
        if (!titleLow.includes(query)) {
          details.push(`Ключ "${r.query}" відсутній у Title`);
        } else if (!titleLow.startsWith(query.split(" ")[0])) {
          details.push(`Ключ не на початку Title`);
        }
        if ((meta.description || "").length < 100) {
          details.push(`Description занадто короткий (${(meta.description || "").length} символів)`);
        }
        actions.push({ label: "Редагувати Title", view: "gen" });

        if (impact > 0) {
          recs.push({
            id: recId++, page: pageUrl, query: r.query, type: "snippet",
            title: `Низький CTR на позиції ${roundPos}`,
            description: details.length ? details.join(". ") : `CTR ${(ctr * 100).toFixed(1)}% проти медіани ${(medianCtr * 100).toFixed(1)}%`,
            impact, position: pos, impressions: imp, actions
          });
        }
      }

      // Scenario 2: Structured snippet — pos 5-10, low CTR
      if (pos >= 5 && pos <= 10 && ctr < medianCtr && medianCtr > 0) {
        const ctrGap = medianCtr - ctr;
        const impact = Math.round(imp * ctrGap / 100);
        const details = [];
        const actions = [];

        if ((meta.title || "").length > 60) details.push(`Title обрізається (${(meta.title || "").length} символів)`);
        if ((meta.description || "").length > 160) details.push(`Description обрізається (${(meta.description || "").length} символів)`);
        const isQuestion = /^(як|що|де|чому|скільки|який|яка|яке|как|что|где|почему|сколько|какой|how|what|where|why|which)/i.test(r.query);
        if (isQuestion) {
          details.push(`Питальний запит — додайте FAQ-розмітку`);
        }
        actions.push({ label: "Редагувати Title", view: "gen" });

        if (impact > 0 && details.length > 0) {
          recs.push({
            id: recId++, page: pageUrl, query: r.query, type: "structured_snippet",
            title: `Покращення спніпета для позиції ${roundPos}`,
            description: details.join(". "),
            impact, position: pos, impressions: imp, actions
          });
        }
      }

      // Scenario 3: Content — pos 8-20, imp > 10, check text
      if (pos >= 8 && pos <= 20 && imp > 10 && hasSeoTexts) {
        const impact = Math.round(imp * 0.02);
        const details = [];
        const actions = [];

        if (textContent && !textContent.includes(query)) {
          details.push(`Запит "${r.query}" відсутній у тексті сторінки`);
          actions.push({ label: "Редагувати текст", view: "seo_editor" });
        } else if (textContent) {
          const h2h3Match = textContent.match(/<h[23][^>]*>([^<]*)<\/h[23]>/gi) || [];
          const inHeading = h2h3Match.some(h => h.toLowerCase().includes(query));
          if (!inHeading) {
            details.push(`Запит "${r.query}" не в заголовках H2/H3`);
            actions.push({ label: "Редагувати текст", view: "seo_editor" });
          }
        }

        if (impact > 0 && details.length > 0) {
          recs.push({
            id: recId++, page: pageUrl, query: r.query, type: "content",
            title: `Зміцнення контенту для "${r.query}"`,
            description: details.join(". "),
            impact, position: pos, impressions: imp, actions
          });
        }
      }

      // Scenario 4: New keyword — query not in semantic core, imp > 20
      if (!coreKeywords.has(query) && imp > 20) {
        const impact = Math.round(imp * 0.03);
        recs.push({
          id: recId++, page: pageUrl, query: r.query, type: "new_keyword",
          title: `Новий ключ "${r.query}"`,
          description: `${imp} показів/міс — не знайдено в семантичному ядрі`,
          impact, position: pos, impressions: imp,
          actions: [{ label: "Переглянути семантику", view: "semantics" }]
        });
      }

      // Scenario 5: Relevance — pos 3-15, keyword mentioned 0-1 times, not in first 500 chars
      if (pos >= 3 && pos <= 15 && hasSeoTexts && textContent) {
        const regex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
        const matches = textContent.match(regex) || [];
        const firstIdx = textContent.indexOf(query);
        if (matches.length <= 1 && (firstIdx < 0 || firstIdx > 500)) {
          const impact = Math.round(imp * 0.02);
          if (impact > 0) {
            recs.push({
              id: recId++, page: pageUrl, query: r.query, type: "relevance",
              title: `Низька релевантність для "${r.query}"`,
              description: matches.length === 0
                ? `Ключ відсутній у тексті сторінки`
                : `Ключ згадується лише ${matches.length} раз, не в перших 500 символах`,
              impact, position: pos, impressions: imp,
              actions: [{ label: "Редагувати текст", view: "seo_editor" }]
            });
          }
        }
      }
    });

    // Scenario 6: Linking — pages with ≥2 shared high N-grams
    if (pageUrls.length <= 200 && Object.keys(pageNgramSets).length > 1) {
      const urls = Object.keys(pageNgramSets);
      for (let i = 0; i < urls.length; i++) {
        for (let j = i + 1; j < urls.length; j++) {
          const setA = pageNgramSets[urls[i]];
          const setB = pageNgramSets[urls[j]];
          if (!setA || !setB) continue;
          const shared = [];
          for (const g of setA) { if (setB.has(g) && g) shared.push(g); }
          if (shared.length >= 2) {
            // Check if link exists between them in html_content
            const textA = (seoTexts[urls[i]]?.html_content || "").toLowerCase();
            const textB = (seoTexts[urls[j]]?.html_content || "").toLowerCase();
            const hasLinkAB = textA.includes(urls[j].replace(/^https?:\/\/[^/]+/, ""));
            const hasLinkBA = textB.includes(urls[i].replace(/^https?:\/\/[^/]+/, ""));
            if (!hasLinkAB || !hasLinkBA) {
              recs.push({
                id: recId++, page: urls[i], query: shared.slice(0, 3).join(", "), type: "linking",
                title: `Перелінковка з ${urls[j].replace(/^https?:\/\/[^/]+/, "")}`,
                description: `Спільні N-грами: ${shared.slice(0, 5).join(", ")}. ${!hasLinkAB ? "Немає посилання A→B" : ""} ${!hasLinkBA ? "Немає посилання B→A" : ""}`.trim(),
                impact: 5, position: 0, impressions: 0,
                actions: [{ label: "Дивитися в GSC", view: "gsc" }]
              });
            }
          }
        }
      }
    }

    return recs;
  }, [gscRows, ctrCurve, pageMeta, coreKeywords, seoTexts, pageNgramSets]);

  // Aggregate by page
  const groupedPages = useMemo(() => {
    let filtered = recommendations;
    if (activeFilter !== "all") filtered = filtered.filter(r => r.type === activeFilter);
    if (searchQ) {
      const q = searchQ.toLowerCase();
      filtered = filtered.filter(r => r.page.toLowerCase().includes(q) || r.query.toLowerCase().includes(q) || r.title.toLowerCase().includes(q));
    }

    const map = {};
    filtered.forEach(r => {
      if (!map[r.page]) map[r.page] = { url: r.page, recs: [], totalImpact: 0 };
      map[r.page].recs.push(r);
      map[r.page].totalImpact += r.impact;
    });

    let pages = Object.values(map);
    if (sortBy === "impact") pages.sort((a, b) => b.totalImpact - a.totalImpact);
    else if (sortBy === "count") pages.sort((a, b) => b.recs.length - a.recs.length);
    else pages.sort((a, b) => a.url.localeCompare(b.url));

    return pages;
  }, [recommendations, activeFilter, searchQ, sortBy]);

  const totalPages = Math.ceil(groupedPages.length / PER_PAGE);
  const visiblePages = groupedPages.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalOpportunities = recommendations.length;
  const totalImpact = recommendations.reduce((s, r) => s + r.impact, 0);
  const totalPagesCount = new Set(recommendations.map(r => r.page)).size;

  const typeBadge = (type) => {
    const styles = {
      snippet: { bg: "#fef3c7", color: "#92400e", label: "Спніпет" },
      structured_snippet: { bg: "#dbeafe", color: "#1e40af", label: "Структура" },
      content: { bg: "#dcfce7", color: "#166534", label: "Контент" },
      new_keyword: { bg: "#f3e8ff", color: "#6b21a8", label: "Новий ключ" },
      relevance: { bg: "#fce7f3", color: "#9d174d", label: "Релевантність" },
      linking: { bg: "#e0e7ff", color: "#3730a3", label: "Перелінковка" },
    };
    const s = styles[type] || { bg: dark ? "#252535" : "#f3f4f6", color: dark ? "#8b8ba0" : "#374151", label: type };
    return <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 600 }}>{s.label}</span>;
  };

  const filterTypes = [
    { key: "all", label: "Всі" },
    { key: "snippet", label: "Спніпет" },
    { key: "structured_snippet", label: "Структура" },
    { key: "content", label: "Контент" },
    { key: "new_keyword", label: "Новий ключ" },
    { key: "relevance", label: "Релевантність" },
    { key: "linking", label: "Перелінковка" },
  ];

  // No GSC data
  if (!loading && gscRows.length === 0) {
    return (
      <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Точки росту</h2>
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Підключіть Google Search Console</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>Для аналізу потрібні дані GSC: позиції, покази, CTR</p>
          <button style={btnP} onClick={() => setView("gsc")}>Підключити GSC</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Точки росту</h2>
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 14, color: t.textMuted, animation: "pulse 1.5s infinite" }}>Завантаження даних...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Точки росту</h2>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
        <div style={{ ...card, marginBottom: 0, textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#6366f1" }}>{totalOpportunities}</div>
          <div style={{ fontSize: 11, color: t.textMuted }}>можливостей</div>
        </div>
        <div style={{ ...card, marginBottom: 0, textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#16a34a" }}>+{totalImpact}</div>
          <div style={{ fontSize: 11, color: t.textMuted }}>кліків/міс</div>
        </div>
        <div style={{ ...card, marginBottom: 0, textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#8b5cf6" }}>{totalPagesCount}</div>
          <div style={{ fontSize: 11, color: t.textMuted }}>сторінок</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {filterTypes.map(ft => (
          <button
            key={ft.key}
            onClick={() => { setActiveFilter(ft.key); setPage(0); }}
            style={{
              ...btnSm,
              background: activeFilter === ft.key ? "#6366f1" : t.bgCard,
              color: activeFilter === ft.key ? "#fff" : t.text,
              border: activeFilter === ft.key ? "1px solid #6366f1" : `1px solid ${t.border}`,
            }}
          >
            {ft.label}
            {ft.key !== "all" && (() => {
              const cnt = recommendations.filter(r => r.type === ft.key).length;
              return cnt > 0 ? ` (${cnt})` : "";
            })()}
          </button>
        ))}
      </div>

      {/* Sort + Search */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          style={{ ...btnG, padding: "4px 10px", fontSize: 11 }}
        >
          <option value="impact">За Impact</option>
          <option value="count">За кількістю</option>
          <option value="url">За URL</option>
        </select>
        <input
          type="text"
          placeholder="Пошук..."
          value={searchQ}
          onChange={e => { setSearchQ(e.target.value); setPage(0); }}
          style={{ flex: 1, padding: "4px 10px", fontSize: 12, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, color: t.text, outline: "none" }}
        />
      </div>

      {/* No results */}
      {visiblePages.length === 0 && (
        <div style={{ ...card, textAlign: "center", padding: 30, color: t.textMuted }}>
          {recommendations.length === 0
            ? "Немає рекомендацій. Переконайтесь, що дані GSC та семантика завантажені."
            : "Нічого не знайдено за обраними фільтрами."
          }
        </div>
      )}

      {/* Grouped pages */}
      {visiblePages.map(pg => {
        const isExpanded = expandedPages[pg.url];
        const shortUrl = pg.url.replace(/^https?:\/\/[^/]+/, "");
        return (
          <div key={pg.url} style={{ ...card, padding: 0, marginBottom: 8 }}>
            {/* Page header */}
            <div
              onClick={() => setExpandedPages(prev => ({ ...prev, [pg.url]: !prev[pg.url] }))}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", cursor: "pointer",
                borderBottom: isExpanded ? `1px solid ${t.border}` : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: t.textMuted, flexShrink: 0 }}>{isExpanded ? "▼" : "▶"}</span>
                <span style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={pg.url}>{shortUrl || pg.url}</span>
                <span style={{ fontSize: 10, color: t.textMuted, flexShrink: 0 }}>{pg.recs.length} рек.</span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", flexShrink: 0 }}>+{pg.totalImpact} кліків</span>
            </div>

            {/* Recommendations list */}
            {isExpanded && (
              <div style={{ padding: "4px 0" }}>
                {pg.recs.sort((a, b) => b.impact - a.impact).map(rec => (
                  <div key={rec.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 14px", borderBottom: `1px solid ${t.border}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        {typeBadge(rec.type)}
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{rec.title}</span>
                      </div>
                      <div style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.4 }}>{rec.description}</div>
                      {rec.query && <div style={{ fontSize: 10, color: "#8b5cf6", marginTop: 2 }}>Запит: {rec.query} | Позиція: {rec.position?.toFixed(1)} | {rec.impressions} показів</div>}
                      <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                        {(rec.actions || []).map((a, i) => (
                          <button key={i} style={{ ...btnSm, fontSize: 10, color: "#6366f1" }} onClick={(e) => { e.stopPropagation(); setView(a.view); }}>
                            {a.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", flexShrink: 0, paddingTop: 2 }}>+{rec.impact}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 12 }}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              style={{
                ...btnSm,
                background: page === i ? "#6366f1" : t.bgCard,
                color: page === i ? "#fff" : t.text,
                minWidth: 28,
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══ CANNIBALIZATION VIEW ═══
